import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import Carousel from '@comp/Carousel/Carousel';
import { Spacings } from '@styles/index';

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const ShimmerBox = styled.div<{
  width?: string;
  height?: string;
  borderRadius?: string;
}>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
  border-radius: ${(props) => props.borderRadius || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnimation} 1s linear infinite;
`;

const SkeletonWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const PrimeContentCardSkeletonWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
`;

const PrimeContentCardMetadata = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TopTrendingSkeletonWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 8px;
  overflow: hidden;
`;

const TopTrendingRankNumber = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  width: 40px;
  height: 40px;
`;

const LiveClassSkeletonWrapper = styled.div`
  width: 100%;
  aspect-ratio: 21 / 9;
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  @media screen and (max-width: 768px) {
    aspect-ratio: 1;
  }
`;

const LiveClassContent = styled.div`
  position: absolute;
  bottom: 24px;
  left: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (max-width: 768px) {
    bottom: 16px;
    left: 16px;
    right: 16px;
  }
`;

const TopEducatorSkeletonWrapper = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
`;

const EducatorHeader = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const EducatorAvatar = styled.div`
  width: 48px;
  height: 48px;
`;

const EducatorInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EducatorLessons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LessonItem = styled.div`
  display: flex;
  gap: 12px;
`;

const LessonThumbnail = styled.div`
  width: 80px;
  height: 45px;
`;

const LessonInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PillsSkeleton = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PillSkeletonItem = styled.div`
  min-width: 80px;
  height: 32px;
  flex-shrink: 0;
`;

const ToolbarSkeletonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
`;

const BadgeSkeletonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0 16px 0;
  box-sizing: border-box;
`;

const SearchBarSkeletonWrapper = styled.div`
  width: 100%;
  padding: 0 16px;
  margin-bottom: 12px;
  box-sizing: border-box;
`;

const SubGoalChipsSkeletonWrapper = styled.div`
  width: 100%;
  padding: 0 16px 8px 16px;
  box-sizing: border-box;
  overflow: hidden;
`;

const FullPageShimmerWrapper = styled.div`
  width: 100%;
  grid-column: 1 / -1;
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;

  @media screen and (max-width: 768px) {
    width: 100%;
    min-height: 100vh;
  }

  & > div {
    width: 100%;
    box-sizing: border-box;
  }

  /* Hide carousel controls in shimmer */
  button[class*='ControlButton'],
  button[class*='StyledIconButton'] {
    display: none !important;
  }
`;

const ContentShimmerWrapper = styled.div`
  width: 100%;
  display: flex;
  grid-column: 1 / -1;
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
  padding-bottom: 16px;
  box-sizing: border-box;
  overflow-x: hidden;

  /* Hide carousel controls in shimmer */
  button[class*='ControlButton'],
  button[class*='StyledIconButton'] {
    display: none !important;
  }
`;

export const PrimeContentCardSkeleton = () => {
  return (
    <SkeletonWrapper>
      <PrimeContentCardSkeletonWrapper>
        <ShimmerBox width="100%" height="100%" borderRadius="8px" />
      </PrimeContentCardSkeletonWrapper>
      <PrimeContentCardMetadata>
        <ShimmerBox width="80%" height="16px" borderRadius="4px" />
        <ShimmerBox width="40%" height="14px" borderRadius="4px" />
      </PrimeContentCardMetadata>
    </SkeletonWrapper>
  );
};

export const TopTrendingCardSkeleton = ({ rank }: { rank: number }) => {
  return (
    <TopTrendingSkeletonWrapper>
      <ShimmerBox width="100%" height="100%" borderRadius="8px" />
      <TopTrendingRankNumber>
        <ShimmerBox width="100%" height="100%" borderRadius="4px" />
      </TopTrendingRankNumber>
    </TopTrendingSkeletonWrapper>
  );
};

export const LiveClassCardSkeleton = () => {
  return (
    <LiveClassSkeletonWrapper>
      <ShimmerBox width="100%" height="100%" borderRadius="12px" />
      <LiveClassContent>
        <ShimmerBox width="300px" height="24px" borderRadius="4px" />
        <ShimmerBox width="120px" height="40px" borderRadius="8px" />
      </LiveClassContent>
    </LiveClassSkeletonWrapper>
  );
};

export const TopEducatorPlaylistCardSkeleton = () => {
  return (
    <TopEducatorSkeletonWrapper>
      <EducatorHeader>
        <EducatorAvatar>
          <ShimmerBox width="100%" height="100%" borderRadius="50%" />
        </EducatorAvatar>
        <EducatorInfo>
          <ShimmerBox width="150px" height="18px" borderRadius="4px" />
          <ShimmerBox width="100px" height="14px" borderRadius="4px" />
        </EducatorInfo>
      </EducatorHeader>
      <EducatorLessons>
        {[1, 2, 3].map((index) => (
          <LessonItem key={index}>
            <LessonThumbnail>
              <ShimmerBox width="100%" height="100%" borderRadius="4px" />
            </LessonThumbnail>
            <LessonInfo>
              <ShimmerBox width="100%" height="14px" borderRadius="4px" />
              <ShimmerBox width="60%" height="12px" borderRadius="4px" />
            </LessonInfo>
          </LessonItem>
        ))}
      </EducatorLessons>
      <div style={{ marginTop: '16px' }}>
        <ShimmerBox width="100px" height="14px" borderRadius="4px" />
      </div>
    </TopEducatorSkeletonWrapper>
  );
};

export const PrimeCarouselSkeleton = ({
  label,
  numberOfItems = 3,
  CardComponent = PrimeContentCardSkeleton,
  mobileItemMinWidth = '160px',
  itemGridColumn,
  gridColumnGap
}: {
  label?: string;
  numberOfItems?: number;
  CardComponent?: React.ComponentType<any>;
  mobileItemMinWidth?: string;
  itemGridColumn?: string;
  gridColumnGap?: string;
}) => {
  return (
    <Carousel
      isPrime
      showBottomGap={false}
      label={label}
      numberOfItems={numberOfItems}
      mobileItemMinWidth={mobileItemMinWidth}
      minItems={1}
      showControls={false}
      itemGridColumn={itemGridColumn}
      gridColumnGap={gridColumnGap}
    >
      {Array.from({ length: numberOfItems }).map((_, index) => (
        <CardComponent key={index} rank={index + 1} />
      ))}
    </Carousel>
  );
};

export const ToolbarSkeleton = () => {
  return (
    <ToolbarSkeletonWrapper>
      <ShimmerBox width="120px" height="24px" borderRadius="4px" />
      <ShimmerBox width="40px" height="40px" borderRadius="50%" />
    </ToolbarSkeletonWrapper>
  );
};

export const BadgeSkeleton = () => {
  return (
    <BadgeSkeletonWrapper>
      <ShimmerBox width="160px" height="32px" borderRadius="16px" />
    </BadgeSkeletonWrapper>
  );
};

export const SearchBarSkeleton = () => {
  return (
    <SearchBarSkeletonWrapper>
      <ShimmerBox width="100%" height="48px" borderRadius="24px" />
    </SearchBarSkeletonWrapper>
  );
};

export const SubGoalChipsSkeleton = () => {
  return (
    <SubGoalChipsSkeletonWrapper>
      <PillsSkeleton>
        {[1, 2, 3, 4, 5].map((index) => (
          <PillSkeletonItem
            key={index}
            style={{ minWidth: `${60 + index * 10}px` }}
          >
            <ShimmerBox width="100%" height="100%" borderRadius="18px" />
          </PillSkeletonItem>
        ))}
      </PillsSkeleton>
    </SubGoalChipsSkeletonWrapper>
  );
};

export const SubCategoryPillsSkeleton = () => {
  return (
    <PillsSkeleton>
      {[1, 2, 3, 4].map((index) => (
        <PillSkeletonItem key={index}>
          <ShimmerBox width="100%" height="100%" borderRadius="16px" />
        </PillSkeletonItem>
      ))}
    </PillsSkeleton>
  );
};

export const ContentShimmer = () => {
  return (
    <ContentShimmerWrapper>
      <PrimeCarouselSkeleton
        label="Top Trending"
        numberOfItems={3}
        CardComponent={TopTrendingCardSkeleton}
        mobileItemMinWidth="140px"
        itemGridColumn="span 4"
        gridColumnGap={Spacings.SPACING_7B}
      />
      <PrimeCarouselSkeleton
        label="Watch special classes"
        numberOfItems={3}
        mobileItemMinWidth="160px"
      />
      {[1, 2].map((index) => (
        <PrimeCarouselSkeleton
          key={index}
          numberOfItems={3}
          mobileItemMinWidth="160px"
        />
      ))}
      <PrimeCarouselSkeleton
        label="Top Educators"
        numberOfItems={1}
        CardComponent={TopEducatorPlaylistCardSkeleton}
        mobileItemMinWidth="330px"
        gridColumnGap={Spacings.SPACING_4B}
      />
    </ContentShimmerWrapper>
  );
};

export const PrimeHomeSkeletons = () => {
  return (
    <FullPageShimmerWrapper>
      <ToolbarSkeleton />
      <BadgeSkeleton />
      <SearchBarSkeleton />
      <SubGoalChipsSkeleton />
      <div style={{ marginTop: '24px', paddingBottom: '24px' }}>
        <Carousel
          isPrime
          numberOfItems={1}
          showInfo={false}
          showBottomGap={false}
          mobileItemMinWidth="100%"
          itemGridColumn="span 18"
          showControls={false}
        >
          <LiveClassCardSkeleton />
        </Carousel>
        <ContentShimmer />
      </div>
    </FullPageShimmerWrapper>
  );
};
